import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Users, Heart, Compass, BookOpen, Award, Target,
  Briefcase, RotateCcw, Flame, GraduationCap, Building, UserCheck,
  ChevronRight, Star, Quote, Sparkles, ArrowRight, Zap, TrendingUp
} from 'lucide-react';

const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_7b2575e8-2794-4e8b-bc83-0e95eaa81cc0/artifacts/58yju9ms_7692917-hd_1920_1080_25fps.mp4";

// Generic business/HR focused images
const IMAGES = {
  teamwork: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80",
  meeting: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
  workspace: "https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&q=80",
  handshake: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80",
  planning: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
  growth: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
};

const whoWeServe = [
  { icon: Briefcase, title: 'Career Growth Seekers', desc: 'Middle-class professionals seeking advancement' },
  { icon: Compass, title: 'Career Transitioners', desc: 'Navigating job changes or pivots' },
  { icon: RotateCcw, title: 'Workforce Returners', desc: 'People returning to the workforce' },
  { icon: Flame, title: 'Burnout Recovery', desc: 'Those experiencing career stagnation' },
  { icon: GraduationCap, title: 'First-Gen Professionals', desc: 'First-generation professionals' },
  { icon: Building, title: 'Life Rebuilders', desc: 'Rebuilding after life disruptions' },
];

const testimonials = [
  {
    quote: "HR Nest helped me rediscover my confidence after years of feeling stuck in my career. The community support is incredible.",
    author: "Sarah M.",
    role: "Career Transitioner"
  },
  {
    quote: "The resources and mentorship I received here were exactly what I needed to land my dream job. Forever grateful!",
    author: "Michelle T.",
    role: "First-Gen Professional"
  },
  {
    quote: "Finally, a place where I felt understood. The coaches here truly care about your growth as a whole person.",
    author: "Jennifer L.",
    role: "Workforce Returner"
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = React.useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section with Video */}
      <section className="relative min-h-[95vh] flex items-center" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover scale-105"
            poster="/placeholder-hero.jpg"
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
          <div className="absolute inset-0 video-overlay" />
          
          {/* Animated 3D Floating Elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-hr-teal/30 rounded-full blur-2xl animate-float-slow" />
          <div className="absolute bottom-40 left-20 w-48 h-48 bg-hr-purple/30 rounded-full blur-3xl animate-float" />
          <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-white/10 rounded-full blur-xl animate-float-fast" />
          <div className="absolute top-1/3 left-1/3 w-16 h-16 bg-hr-teal/20 rounded-full blur-lg animate-pulse" />
          <div className="absolute bottom-1/4 right-1/3 w-20 h-20 bg-hr-purple/20 rounded-full blur-xl animate-bounce-slow" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            {/* Badge with pulse animation */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8 animate-fade-in-up animate-pulse-subtle">
              <Sparkles className="h-4 w-4 text-hr-teal animate-spin-slow" />
              <span className="text-white/90 text-sm font-medium">Where People Come First</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight animate-fade-in-up">
              Welcome to HR Nest, where you can{' '}
              <span className="text-gradient bg-gradient-to-r from-hr-teal via-cyan-300 to-hr-teal bg-clip-text text-transparent animate-gradient inline-block animate-shimmer-text">
                reset, rebuild, and rise.
              </span>
            </h1>
            
            <p className="mt-8 text-lg sm:text-xl text-white/90 leading-relaxed animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
              Life transitions can feel overwhelming, but you were never meant to walk them alone. Through resources, mentorship, and community, HR Nest supports individuals as they build confidence, discover direction, and move forward with dignity and purpose.
            </p>
            
            <p className="mt-4 text-lg text-white/70 animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
              No matter where you start from, there is a path forward, and we are here to help you find it.
            </p>

            <div className="mt-12 flex flex-wrap gap-4 animate-fade-in-up stagger-4" style={{ opacity: 0 }}>
              <Link to="/services" data-testid="hero-explore-services-btn">
                <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-7 text-lg font-medium shadow-2xl hover:shadow-hr-purple/50 transition-all duration-500 hover:-translate-y-2 hover:scale-105 btn-3d animate-glow">
                  Explore Services
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-2 group-hover:scale-110" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

      </section>

      {/* What Makes HR Nest Different */}
      <section className="py-28 bg-white relative overflow-hidden" data-testid="what-makes-different-section">
        {/* Animated background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-hr-purple/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-hr-teal/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-pulse-slow stagger-2" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-hr-purple/3 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <Zap className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Our Difference</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary animate-fade-in-up">
              What Makes HR Nest{' '}
              <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Different</span>
            </h2>
            <p className="mt-6 text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1">
              HR Nest focuses on the whole person, not just employment outcomes. We are here to help and that is why our services are offered at the lowest price compared to other competitors offering the same services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Heart, title: 'People-Centered Guidance', desc: 'Personalized support that sees you as a whole person', color: 'purple' },
              { icon: Users, title: 'Supportive Community', desc: 'Connect with others on similar journeys', color: 'teal' },
              { icon: BookOpen, title: 'Real-World Tools', desc: 'Practical templates and resources you can use today', color: 'purple' },
              { icon: Award, title: 'Education & Empowerment', desc: 'Learn from real-life experiences and expert guidance', color: 'teal' },
            ].map((item, index) => (
              <Card 
                key={index} 
                className="group bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 card-3d overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
              >
                <CardContent className="p-8 text-center relative">
                  {/* Animated background gradient on hover */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 ${
                    item.color === 'purple' ? 'bg-gradient-to-br from-hr-purple/10 via-transparent to-hr-teal/5' : 'bg-gradient-to-br from-hr-teal/10 via-transparent to-hr-purple/5'
                  }`} />
                  
                  <div className={`relative w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-xl ${
                    item.color === 'purple' ? 'bg-hr-purple-light group-hover:bg-hr-purple' : 'bg-hr-teal-light group-hover:bg-hr-teal'
                  }`}>
                    <item.icon className={`h-10 w-10 transition-all duration-500 group-hover:scale-110 ${
                      item.color === 'purple' ? 'text-hr-purple group-hover:text-white' : 'text-hr-teal group-hover:text-white'
                    }`} />
                  </div>
                  <h3 className="relative font-heading text-xl font-semibold text-hr-text-primary mb-3 group-hover:text-hr-purple transition-colors duration-300">{item.title}</h3>
                  <p className="relative text-hr-text-secondary text-sm">{item.desc}</p>
                  
                  {/* Bottom accent line animation */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-0 group-hover:w-1/2 transition-all duration-500 rounded-full ${
                    item.color === 'purple' ? 'bg-hr-purple' : 'bg-hr-teal'
                  }`} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-28 bg-gradient-to-br from-hr-warm-bg via-white to-hr-warm-bg relative overflow-hidden" data-testid="our-approach-section">
        {/* Animated background shapes */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-hr-purple/10 rounded-full blur-3xl animate-blob" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-blob stagger-3" />
        <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-float" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="animate-slide-left" style={{ opacity: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6 animate-bounce-subtle">
                <Target className="h-4 w-4 text-hr-teal animate-pulse" />
                <span className="text-hr-teal text-sm font-medium">Our Philosophy</span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary mb-8">
                Our <span className="text-hr-teal animate-pulse-subtle">Approach</span>
              </h2>
              
              <p className="text-lg text-hr-text-secondary mb-8">
                At HR Nest, we believe:
              </p>

              <ul className="space-y-5">
                {[
                  'Preparation creates opportunity',
                  'Confidence is built through support and skill',
                  'Lived experience is a powerful teacher',
                  'Growth happens best in community'
                ].map((belief, index) => (
                  <li key={index} className="flex items-start gap-4 group animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-hr-purple to-hr-teal flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <ChevronRight className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg text-hr-text-primary group-hover:text-hr-purple transition-colors duration-300 group-hover:translate-x-2 transform">{belief}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-5" style={{ opacity: 0 }}>
                Everything we offer is grounded in real-world application, encouragement, and accountability—designed to turn readiness into results.
              </p>

              <p className="mt-4 text-xl font-semibold text-hr-purple animate-fade-in-up stagger-6" style={{ opacity: 0 }}>
                Our goal is to help people make informed decisions, build confidence, and move forward with clarity.
              </p>
            </div>

            <div className="relative animate-slide-right" style={{ opacity: 0 }}>
              {/* Main Image with 3D effects */}
              <div className="relative z-10">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-700 hover:shadow-hr-purple/30">
                  <img 
                    src={IMAGES.teamwork}
                    alt="Team collaboration and growth"
                    className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
                  />
                  {/* Overlay with gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-hr-purple/20 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                {/* Floating accent image */}
                <div className="absolute -bottom-10 -left-10 w-44 h-44 rounded-2xl overflow-hidden shadow-2xl animate-float z-20 border-4 border-white hover:scale-110 transition-transform duration-500">
                  <img 
                    src={IMAGES.handshake}
                    alt="Professional handshake"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Animated Stats card */}
                <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl z-20 animate-float-slow hover:scale-110 transition-transform duration-500">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-hr-purple">
                      <AnimatedCounter end={10} suffix="+" />
                    </div>
                    <div className="text-sm text-hr-text-muted mt-1">Years Experience</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative animated elements */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-hr-teal/20 rounded-2xl -z-10 animate-float rotate-12" />
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-hr-purple/20 rounded-2xl -z-10 animate-float-slow -rotate-6" />
              <div className="absolute top-1/2 -right-4 w-8 h-8 bg-hr-teal rounded-full animate-ping opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-28 bg-white relative overflow-hidden" data-testid="who-we-serve-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6">
              <Users className="h-4 w-4 text-hr-purple" />
              <span className="text-hr-purple text-sm font-medium">Our Community</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary">
              Who We <span className="text-hr-purple">Serve</span>
            </h2>
            <p className="mt-6 text-lg text-hr-text-secondary">
              We support individuals at every stage of their career journey
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whoWeServe.map((item, index) => (
              <Card 
                key={index} 
                className="group border border-stone-100 hover:border-hr-purple/30 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
              >
                <CardContent className="p-6 flex items-start gap-4 relative">
                  {/* Animated hover gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-hr-purple/5 via-transparent to-hr-teal/5 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-hr-purple-light to-hr-teal-light flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg group-hover:shadow-xl">
                    <item.icon className="h-7 w-7 text-hr-purple group-hover:text-hr-teal transition-colors duration-500" />
                  </div>
                  <div className="relative">
                    <h3 className="font-heading text-lg font-semibold text-hr-text-primary mb-1 group-hover:text-hr-purple transition-colors duration-300">{item.title}</h3>
                    <p className="text-sm text-hr-text-muted">{item.desc}</p>
                  </div>
                  
                  {/* Arrow indicator */}
                  <ArrowRight className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-hr-purple opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 animate-fade-in-up stagger-6" style={{ opacity: 0 }}>
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-hr-teal-light to-hr-purple-light text-hr-teal hover:scale-105 transition-transform duration-300">
              <UserCheck className="h-5 w-5 animate-bounce-subtle" />
              <span className="font-medium">Anyone seeking practical career and personal development support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-20 bg-hr-warm-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-700 hover:-translate-y-2 group">
              <img 
                src={IMAGES.meeting}
                alt="Professional business meeting"
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:rotate-1 group">
              <img 
                src={IMAGES.workspace}
                alt="Modern workspace"
                className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:-rotate-1 group">
              <img 
                src={IMAGES.handshake}
                alt="Business handshake"
                className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:rotate-1 group">
              <img 
                src={IMAGES.planning}
                alt="Career planning"
                className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-hr-purple to-hr-teal flex items-center justify-center p-8 group hover:rotate-1">
              <div className="text-center text-white transform group-hover:scale-110 transition-transform duration-500">
                <div className="text-5xl font-bold mb-2">
                  <AnimatedCounter end={100} suffix="+" />
                </div>
                <div className="text-white/80">Success Stories</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 bg-gradient-to-br from-hr-purple via-hr-purple-dark to-hr-purple relative overflow-hidden" data-testid="testimonials-section">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-blob" />
          <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-hr-teal/20 rounded-full blur-xl animate-ping opacity-20" />
          <div className="absolute bottom-1/3 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-lg animate-bounce-slow" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 mb-6">
              <Quote className="h-4 w-4 text-hr-teal" />
              <span className="text-white/90 text-sm font-medium">Testimonials</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
              What Our Community Says
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="group glass-dark border-white/10 hover:border-white/30 transition-all duration-500 hover:-translate-y-4 hover:scale-[1.02] animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s`, opacity: 0 }}
              >
                <CardContent className="p-8 relative overflow-hidden">
                  {/* Animated corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-hr-teal/20 rounded-bl-full transform translate-x-10 -translate-y-10 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500" />
                  
                  <Quote className="h-12 w-12 text-hr-teal mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500" />
                  <p className="text-white/90 text-lg leading-relaxed mb-8">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-hr-teal text-hr-teal animate-pulse-subtle" style={{ animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                  <p className="font-semibold text-white text-lg">{testimonial.author}</p>
                  <p className="text-white/60">{testimonial.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 bg-hr-warm-bg relative overflow-hidden" data-testid="cta-section">
        {/* Animated decorative elements */}
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-hr-purple/10 rounded-full blur-3xl animate-blob" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-hr-teal/10 rounded-full blur-3xl animate-blob stagger-2" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-hr-purple/5 to-hr-teal/5 rounded-full blur-3xl animate-spin-very-slow" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-8 animate-bounce-subtle">
            <TrendingUp className="h-4 w-4 text-hr-purple animate-pulse" />
            <span className="text-hr-purple text-sm font-medium">Start Your Journey Today</span>
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary mb-6">
            Ready to Take the{' '}
            <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Next Step</span>?
          </h2>
          <p className="text-lg text-hr-text-secondary mb-12 max-w-2xl mx-auto">
            Join our community of individuals who are resetting, rebuilding, and rising together. Your transformation starts here.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services" data-testid="cta-explore-services-btn">
              <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-10 py-7 text-lg font-medium shadow-2xl hover:shadow-hr-purple/40 transition-all duration-500 hover:-translate-y-3 hover:scale-105 btn-3d animate-glow">
                Explore Services
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
